import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  FileText,
  Users,
  TrendingUp,
  CheckCircle,
  User,
  Mail,
  Plus,
  Trash2,
  UserPlus,
} from "lucide-react"
import { Layout } from "@/shared/components"
import { ClientEnrollmentWizard } from "@/features/agents"
import { useAuth } from "@/features/auth"
import { useCampaign, Campaign } from "@/features/campaigns"
import { EnrollmentApi } from "@/features/enrollment"
import { apiService } from "@/shared/services/api/api.service"

export default function UserDashboard() {
  const { user } = useAuth()
  const { campaigns, isLoading: campaignsLoading } = useCampaign()
  const navigate = useNavigate()

  // Check if user needs to complete onboarding
  useEffect(() => {
    if (user && user.role === 'user') {
      if (user.onboardingStep === 3) {
        navigate('/onboarding/step-3')
      } else if (user.onboardingStep === 4) {
        navigate('/onboarding/step-4')
      } else if (user.onboardingStep === 5) {
        navigate('/onboarding/step-5')
      } else if (user.onboardingStep === 6) {
        navigate('/onboarding/step-6')
      }
    }
  }, [user, navigate])

  // Get initial tab from URL hash or default to overview
  const getInitialTab = () => {
    const hash = window.location.hash.replace('#', '')
    const validTabs = ["overview", "self-enrollment", "refer-user"]
    return validTabs.includes(hash) ? hash : "overview"
  }

  const [activeTab, setActiveTab] = useState(getInitialTab)
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)
  const [assignedCampaign, setAssignedCampaign] = useState<Campaign | null>(null)

  // Dashboard statistics state
  const [dashboardStats, setDashboardStats] = useState({
    myEnrollments: 0,
    completed: 0,
    referralsMade: 0
  })
  const [statsLoading, setStatsLoading] = useState(true)

  // Referral functionality state
  const [newReferral, setNewReferral] = useState('')
  const [creatingAccounts, setCreatingAccounts] = useState(false)
  const [accountsCreated, setAccountsCreated] = useState<string[]>([])
  const [referralEmails, setReferralEmails] = useState<string[]>([])

  // Get assigned campaign for user
  useEffect(() => {
    if (!campaignsLoading && user && campaigns.length > 0) {
      if (user.campaignId) {
        const campaign = campaigns.find(c => c.id === user.campaignId)
        if (campaign) {
          setAssignedCampaign(campaign)
        }
      }
    }
  }, [campaignsLoading, user, campaigns])

  // Fetch dashboard statistics
  useEffect(() => {
    const fetchDashboardStats = async () => {
      try {
        setStatsLoading(true)
        const response = await apiService.get<{ success: boolean; data: any }>('/users/dashboard-stats')
        if (response.data.success) {
          setDashboardStats(response.data.data)
        }
      } catch (error) {
        console.error('Error fetching dashboard stats:', error)
      } finally {
        setStatsLoading(false)
      }
    }

    if (user) {
      fetchDashboardStats()
    }
  }, [user])

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId)
    // Update URL hash without triggering page reload
    window.history.replaceState(null, '', `#${tabId}`)
  }

  // Referral functions
  const addReferral = () => {
    const email = newReferral.trim()
    if (!email) return
    const next = Array.from(new Set([...referralEmails, email]))
    setReferralEmails(next)
    setNewReferral('')
  }

  const removeReferral = (email: string) => {
    const next = referralEmails.filter((e) => e !== email)
    setReferralEmails(next)
  }

  const createUserAccountsForReferrals = async () => {
    if (!referralEmails || referralEmails.length === 0) {
      alert('No referral emails to create accounts for.')
      return
    }

    setCreatingAccounts(true)
    const createdEmails: string[] = []

    try {
      for (const email of referralEmails) {
        try {
          await EnrollmentApi.createUserAccountForReferral(email)
          createdEmails.push(email)
        } catch (error) {
          console.error(`Failed to create account for ${email}:`, error)
          // Continue with other emails even if one fails
        }
      }

      setAccountsCreated(createdEmails)

      if (createdEmails.length > 0) {
        alert(`Successfully created user accounts for ${createdEmails.length} referral(s). Login details have been sent to their emails.`)
      }

      if (createdEmails.length < referralEmails.length) {
        alert(`Some accounts could not be created. ${createdEmails.length}/${referralEmails.length} accounts were created successfully.`)
      }
    } catch (error) {
      console.error('Error creating user accounts:', error)
      alert('Failed to create user accounts. Please try again.')
    } finally {
      setCreatingAccounts(false)
    }
  }

  const navigationItems = [
    { id: "overview", label: "Overview", icon: TrendingUp },
    { id: "self-enrollment", label: "Self Enrollment", icon: FileText },
    { id: "refer-user", label: "Refer User", icon: Users },
  ]

  const renderContent = () => {
    switch (activeTab) {
      case "overview":
        return (
          <div className="space-y-6">
            {/* Quick Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              <div className="card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-accent-600">My Enrollments</p>
                    <p className="text-2xl font-bold text-accent-900">
                      {statsLoading ? '...' : dashboardStats.myEnrollments}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                    <FileText className="w-6 h-6 text-primary-600" />
                  </div>
                </div>
              </div>
              <div className="card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-accent-600">Completed</p>
                    <p className="text-2xl font-bold text-accent-900">
                      {statsLoading ? '...' : dashboardStats.completed}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </div>
              <div className="card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-accent-600">Referrals Made</p>
                    <p className="text-2xl font-bold text-accent-900">
                      {statsLoading ? '...' : dashboardStats.referralsMade}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-secondary-100 rounded-lg flex items-center justify-center">
                    <Users className="w-6 h-6 text-secondary-600" />
                  </div>
                </div>
              </div>
            </div>

            {/* Welcome Section */}
            <div className="card">
              <div className="flex flex-col sm:flex-row items-center sm:items-start space-y-4 sm:space-y-0 sm:space-x-4 mb-6">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <User className="w-8 h-8 text-blue-600" />
                </div>
                <div className="text-center sm:text-left">
                  <h2 className="text-xl sm:text-2xl font-bold text-accent-900">Welcome to Your Dashboard</h2>
                  <p className="text-sm sm:text-base text-accent-600">Complete your enrollment and refer others to get started</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-blue-900 mb-3">Self Enrollment</h3>
                  <p className="text-blue-800 mb-4">
                    Complete your enrollment form to submit your gaming injury claim. This process will help us understand your gaming history and any potential injuries related to video game use.
                  </p>
                  <button
                    onClick={() => handleTabChange('self-enrollment')}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2 rounded-lg transition-colors"
                  >
                    Start Enrollment
                  </button>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-green-900 mb-3">Refer Others</h3>
                  <p className="text-green-800 mb-4">
                    Help others by referring them to our enrollment system. Share your experience and help them get the support they need.
                  </p>
                  <button
                    onClick={() => handleTabChange('refer-user')}
                    className="bg-green-600 hover:bg-green-700 text-white font-medium px-4 py-2 rounded-lg transition-colors"
                  >
                    Refer User
                  </button>
                </div>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="card">
              <h3 className="text-lg font-semibold text-accent-900 mb-4">Recent Activity</h3>
              <div className="space-y-3 sm:space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 bg-accent-50 rounded-lg">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-accent-900">Enrollment completed</p>
                      <p className="text-xs text-accent-600 truncate">Your enrollment form has been submitted successfully</p>
                    </div>
                  </div>
                  <span className="text-xs text-accent-500 sm:text-right">2 hours ago</span>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 bg-accent-50 rounded-lg">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <Users className="w-5 h-5 text-primary-600 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-accent-900">User referred</p>
                      <p className="text-xs text-accent-600 truncate">john.doe@example.com</p>
                    </div>
                  </div>
                  <span className="text-xs text-accent-500 sm:text-right">1 day ago</span>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 bg-accent-50 rounded-lg">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <FileText className="w-5 h-5 text-secondary-600 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-accent-900">Enrollment started</p>
                      <p className="text-xs text-accent-600 truncate">You began your enrollment process</p>
                    </div>
                  </div>
                  <span className="text-xs text-accent-500 sm:text-right">3 days ago</span>
                </div>
              </div>
            </div>
          </div>
        )
      case "self-enrollment":
        return assignedCampaign ? (
          <ClientEnrollmentWizard
            selectedCampaign={assignedCampaign}
            onCampaignSelect={() => {
              // Users cannot change their campaign
            }}
          />
        ) : (
          <div className="card">
            <div className="text-center py-8">
              <p className="text-accent-600">No campaign assigned. Please contact support.</p>
            </div>
          </div>
        )
      case "refer-user":
        return (
          <div className="card">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-primary-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-accent-900">Refer User</h2>
                <p className="text-accent-600">Add referral email addresses to send enrollment invitations</p>
              </div>
            </div>

            <div className="bg-primary-50 border border-primary-200 rounded p-4 space-y-3">
              <label className="block text-sm font-medium text-primary-900">Referral Email Addresses</label>
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="relative flex-1">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-primary-400 w-4 h-4" />
                  <input
                    type="email"
                    className="input-field pl-10 w-full text-sm sm:text-base"
                    placeholder="Enter referral email address"
                    value={newReferral}
                    onChange={(e) => setNewReferral(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addReferral() } }}
                  />
                </div>
                <button className="btn-primary flex items-center justify-center gap-2 w-full sm:w-auto" onClick={addReferral}>
                  <Plus className="w-4 h-4" /> <span>Add</span>
                </button>
              </div>

              {referralEmails && referralEmails.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {referralEmails.map((email) => (
                    <span key={email} className="inline-flex items-center gap-2 bg-white border border-primary-200 text-primary-800 px-2 py-1 rounded">
                      {email}
                      <button className="text-primary-500 hover:text-primary-700" onClick={() => removeReferral(email)} title="Remove">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              <p className="text-xs text-primary-600">
                You can add up to 10 emails. The system will send self-enrollment invites with your referral credit.
              </p>

              {/* Create User Accounts Button */}
              {referralEmails && referralEmails.length > 0 && (
                <div className="mt-4 pt-4 border-t border-primary-200">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex-1">
                      <h5 className="font-medium text-primary-900 mb-1">Create User Accounts</h5>
                      <p className="text-xs text-primary-600">
                        Create login accounts for referrals. They will receive email with login credentials.
                      </p>
                    </div>
                    <button
                      onClick={createUserAccountsForReferrals}
                      disabled={creatingAccounts}
                      className={`flex items-center justify-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors w-full sm:w-auto flex-shrink-0 ${creatingAccounts
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : accountsCreated.length > 0
                            ? 'bg-green-100 text-green-700 border border-green-300'
                            : 'bg-primary-600 hover:bg-primary-700 text-white'
                        }`}
                    >
                      {creatingAccounts ? (
                        <>
                          <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                          <span>Creating...</span>
                        </>
                      ) : accountsCreated.length > 0 ? (
                        <>
                          <CheckCircle className="w-4 h-4" />
                          <span>Accounts Created ({accountsCreated.length})</span>
                        </>
                      ) : (
                        <>
                          <UserPlus className="w-4 h-4" />
                          <span>Send Referral Invites</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )
      default:
        return (
          <div className="card">
            <h2 className="text-xl font-semibold text-accent-900 mb-4">{activeTab.replace("-", " ").toUpperCase()}</h2>
            <div className="bg-accent-50 p-4 rounded-lg">
              <p className="text-sm text-accent-700">
                {activeTab.replace("-", " ")} functionality will be implemented here.
              </p>
            </div>
          </div>
        )
    }
  }

  return (
    <Layout title="User Dashboard" onMenuToggle={() => setMobileSidebarOpen(!mobileSidebarOpen)}>
      {/* Overlay for mobile sidebar */}
      {mobileSidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-0 lg:hidden" onClick={() => setMobileSidebarOpen(false)} />
      )}

      <div className="flex flex-1">
        {/* Sidebar Navigation - Fixed and Responsive */}
        <div
          className={`fixed top-0 sm:top-16 left-0 w-72 h-screen sm:h-[calc(100vh-4rem)] overflow-y-auto bg-gradient-to-b from-slate-50 to-slate-100 border-r border-slate-300 p-6 z-10
          ${mobileSidebarOpen ? "block" : "hidden"} lg:block`}
        >
          {/* Mobile header space */}
          <div className="h-14 sm:hidden"></div>
          <nav className="space-y-2">
            {navigationItems.map((item) => {
              const Icon = item.icon
              const isActive = activeTab === item.id
              return (
                <button
                  key={item.id}
                  onClick={() => {
                    handleTabChange(item.id)
                    setMobileSidebarOpen(false)
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left rounded-xl transition-all duration-200 ${isActive
                      ? "bg-blue-600 text-white shadow-md"
                      : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                    }`}
                  data-nav-id={item.id}
                >
                  <Icon className={`w-5 h-5 flex-shrink-0 ${isActive ? "text-white" : "text-slate-500"
                    }`} />
                  <span className="font-medium text-sm whitespace-nowrap overflow-hidden text-ellipsis">
                    {item.label}
                  </span>
                </button>
              )
            })}
          </nav>
        </div>
        {/* Main Content Area - Scrolls independently and adjusts for sidebar */}
        <div className="flex-1 lg:ml-72 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-14 sm:pt-16">{renderContent()}</div>
      </div>
    </Layout>
  )
}
